import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { canUserCreateTripPlan, deleteTripPlan, acceptInvitation } from '../../utils/tripPlanUtils';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import TripPlanModal from './TripPlanModal';
import TripChecklist from './TripChecklist';
import BookedGirlDisplay from './BookedGirlDisplay';
import SimpleInviteModal from './SimpleInviteModal';
import HelperManagementModal from './HelperManagementModal';
import HelperGirlProfiles from './HelperGirlProfiles';
import HelperDisplayName from './HelperDisplayName';

const TripPlansPage = () => {
  const { currentUser } = useAuth();
  const [tripPlans, setTripPlans] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showHelperModal, setShowHelperModal] = useState(false);
  const [selectedTripPlan, setSelectedTripPlan] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deletingTripId, setDeletingTripId] = useState(null);
  const [processingInvitation, setProcessingInvitation] = useState(null);

  // Check if user can create trip plans
  const canCreate = canUserCreateTripPlan(currentUser?.role);

  // Fetch pending invitations for current user
  const fetchPendingInvitations = useCallback(async () => {
    if (!currentUser?.email) return;

    try {
      const tripPlansRef = collection(db, 'tripPlans');
      const querySnapshot = await getDocs(tripPlansRef);
      const invitations = [];

      querySnapshot.forEach(doc => {
        const tripPlanData = doc.data();
        if (tripPlanData.invitations && Array.isArray(tripPlanData.invitations)) {
          tripPlanData.invitations.forEach(invitation => {
            if (invitation.email === currentUser.email && invitation.status === 'pending') {
              invitations.push({
                id: `${doc.id}_${invitation.token}`,
                tripPlanId: doc.id,
                tripName: tripPlanData.tripName,
                tripDate: tripPlanData.tripDate,
                createdBy: tripPlanData.createdBy,
                invitation: invitation
              });
            }
          });
        }
      });

      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
    }
  }, [currentUser?.email]);

  useEffect(() => {
    if (!currentUser) return;

    const tripPlansRef = collection(db, 'tripPlans');
    
    // Get all trip plans (no filter) to include both owned and helper trips
    const unsubscribe = onSnapshot(tripPlansRef, (querySnapshot) => {
      const allPlans = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(plan => plan.id && plan.id !== '');
      
      // Filter out dummy data or invalid trip plans
      const validPlans = allPlans.filter(plan => 
        plan.id && 
        plan.id !== '' && 
        plan.tripName && 
        plan.tripName.trim() !== ''
      );
      
      // Filter to show only trips where user is owner or helper
      const userPlans = validPlans.filter(plan => {
        // User is owner
        if (plan.userId === currentUser.uid) return true;
        
        // User is helper
        if (plan.helpers && Array.isArray(plan.helpers)) {
          return plan.helpers.some(helper => helper.userId === currentUser.uid);
        }
        
        return false;
      });
      
      // Sort by creation date (newest first)
      userPlans.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setTripPlans(userPlans);
      setLoading(false);
    });

    // Fetch pending invitations
    fetchPendingInvitations();

    return () => unsubscribe();
  }, [currentUser, fetchPendingInvitations]);

  const handleDeleteTripPlan = async (tripPlanId) => {
    if (!tripPlanId) {
      alert('ไม่พบ ID ของทริป');
      return;
    }

    if (window.confirm('คุณแน่ใจหรือไม่ที่จะลบทริปนี้?')) {
      setDeletingTripId(tripPlanId);
      try {
        const result = await deleteTripPlan(tripPlanId);
        if (result.success) {
          alert('ลบทริปสำเร็จ');
        } else {
          alert('เกิดข้อผิดพลาด: ' + result.error);
        }
      } catch (error) {
        console.error('Error deleting trip plan:', error);
        alert('เกิดข้อผิดพลาดในการลบทริป');
      } finally {
        setDeletingTripId(null);
      }
    }
  };

  const handleViewTripDetails = (tripPlan) => {
    setSelectedTripPlan(tripPlan);
    setShowEditModal(true);
  };

  const handleUpdateChecklist = (tripPlan) => {
    setSelectedTripPlan(tripPlan);
    setShowChecklistModal(true);
  };

  const handleInviteHelper = (tripPlan) => {
    setSelectedTripPlan(tripPlan);
    setShowInviteModal(true);
  };

  const handleManageHelpers = (tripPlan) => {
    setSelectedTripPlan(tripPlan);
    setShowHelperModal(true);
  };

  const handleAcceptInvitation = async (invitationId, token) => {
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบก่อน');
      return;
    }

    setProcessingInvitation(invitationId);
    try {
      console.log('Accepting invitation with token:', token);
      console.log('User info:', { uid: currentUser.uid, email: currentUser.email });
      
      const result = await acceptInvitation(token, currentUser.uid, currentUser.email);
      console.log('Accept invitation result:', result);
      
      if (result.success) {
        alert('ยอมรับคำเชิญสำเร็จ! ตอนนี้คุณสามารถช่วยจัดการทริปนี้ได้แล้ว');
        // Refresh invitations
        fetchPendingInvitations();
      } else {
        alert('เกิดข้อผิดพลาด: ' + result.error);
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      alert('เกิดข้อผิดพลาดในการยอมรับคำเชิญ: ' + error.message);
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    if (window.confirm('คุณแน่ใจหรือไม่ที่จะปฏิเสธคำเชิญนี้?')) {
      setProcessingInvitation(invitationId);
      try {
        // Remove invitation from pending list (you can implement decline logic later)
        setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
        alert('ปฏิเสธคำเชิญเรียบร้อย');
      } catch (error) {
        console.error('Error declining invitation:', error);
        alert('เกิดข้อผิดพลาดในการปฏิเสธคำเชิญ');
      } finally {
        setProcessingInvitation(null);
      }
    }
  };


  const filteredTripPlans = tripPlans.filter(plan => {
    return statusFilter === 'all' || plan.status === statusFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'ใช้งาน';
      case 'draft': return 'ร่าง';
      case 'completed': return 'เสร็จสิ้น';
      case 'cancelled': return 'ยกเลิก';
      default: return 'ไม่ทราบ';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'ไม่ระบุ';
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('th-TH');
  };

  const getProgressFromChecklist = (checklist) => {
    if (!checklist) return 0;
    
    // Count total items and completed items from all steps
    let totalItems = 0;
    let completedItems = 0;
    
    Object.values(checklist).forEach(step => {
      if (typeof step === 'object') {
        const stepItems = Object.values(step);
        totalItems += stepItems.length;
        completedItems += stepItems.filter(item => item === true).length;
      }
    });
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  const getStepProgress = (stepChecklist) => {
    if (!stepChecklist || typeof stepChecklist !== 'object') return 0;
    
    const items = Object.values(stepChecklist);
    const completedItems = items.filter(item => item === true).length;
    const totalItems = items.length;
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };


  const isStepActive = (stepNumber, checklist) => {
    if (!checklist) return stepNumber === 1;
    
    const step1Progress = getStepProgress(checklist?.planTrip || {});
    const step2Progress = getStepProgress(checklist?.prepareTrip || {});
    
    // Step 1 is always active if trip exists
    if (stepNumber === 1) return true;
    
    // Step 2 is active if Step 1 is complete
    if (stepNumber === 2) return step1Progress === 100;
    
    // Step 3 is active if Step 2 is complete
    if (stepNumber === 3) return step2Progress === 100;
    
    return false;
  };

  // Calculate statistics
  const totalTrips = tripPlans.length;
  const completedTrips = tripPlans.filter(plan => plan.status === 'completed').length;
  const activeTrips = tripPlans.filter(plan => plan.status === 'active').length;
  const averageProgress = tripPlans.length > 0 
    ? Math.round(tripPlans.reduce((sum, plan) => sum + getProgressFromChecklist(plan.checklist), 0) / tripPlans.length)
    : 0;

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ไม่สามารถเข้าถึงได้</h2>
          <p className="text-gray-600">คุณไม่มีสิทธิ์ในการจัดการ Trip Plans</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Trip Plan Management</h1>
              <p className="text-gray-600 mt-2">จัดการทริปของคุณอย่างเป็นระบบ</p>
            </div>
              <button
                onClick={() => setShowCreateModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
            >
              <span className="mr-2">+</span>
              สร้างทริปใหม่
            </button>
          </div>
        </div>

        {/* Pending Invitations Section */}
        {pendingInvitations.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg mr-3">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-yellow-800">คำเชิญที่รอการตอบรับ</h2>
              <span className="ml-2 px-2 py-1 bg-yellow-200 text-yellow-800 text-sm rounded-full">
                {pendingInvitations.length}
              </span>
            </div>
            
            <div className="space-y-4">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} className="bg-white rounded-lg border border-yellow-200 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        ทริป: {invitation.tripName}
                      </h3>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center">
                          <span className="mr-2">📅</span>
                          <span>วันที่ทริป: {formatDate(invitation.tripDate)}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-2">👤</span>
                          <span>ผู้เชิญ: {invitation.createdBy}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-2">⏰</span>
                          <span>ได้รับเชิญเมื่อ: {formatDate(invitation.invitation.invitedAt)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-medium text-green-800 mb-2">สิทธิ์ที่คุณจะได้รับ:</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li className="flex items-center">
                            <span className="mr-2">✅</span>
                            ช่วยทำ checklist ทริป
                          </li>
                          <li className="flex items-center">
                            <span className="mr-2">✅</span>
                            จองคิวน้องแทนผู้จัดทริป
                          </li>
                          <li className="flex items-center">
                            <span className="mr-2">✅</span>
                            ดูข้อมูลทริปทั้งหมด
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => handleAcceptInvitation(invitation.id, invitation.invitation.token)}
                        disabled={processingInvitation === invitation.id}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                          processingInvitation === invitation.id
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {processingInvitation === invitation.id ? 'กำลังดำเนินการ...' : 'ยอมรับคำเชิญ'}
                      </button>
                      <button
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        disabled={processingInvitation === invitation.id}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                          processingInvitation === invitation.id
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                      >
                        {processingInvitation === invitation.id ? 'กำลังดำเนินการ...' : 'ปฏิเสธคำเชิญ'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">ทริปทั้งหมด</p>
                <p className="text-2xl font-bold text-gray-900">{totalTrips}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">ทริปที่เสร็จสิ้น</p>
                <p className="text-2xl font-bold text-gray-900">{completedTrips}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">ทริปที่ใช้งาน</p>
                <p className="text-2xl font-bold text-gray-900">{activeTrips}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">ความคืบหน้าเฉลี่ย</p>
                <p className="text-2xl font-bold text-gray-900">{averageProgress}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">กรองทริป</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ทั้งหมด
            </button>
            <button
              onClick={() => setStatusFilter('draft')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'draft' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ร่าง
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'active' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ใช้งาน
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'completed' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              เสร็จสิ้น
            </button>
            <button
              onClick={() => setStatusFilter('cancelled')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'cancelled' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ยกเลิก
              </button>
          </div>
        </div>

        {/* Trip Plans Grid */}
        <div className="grid grid-cols-1 gap-6">
          {filteredTripPlans.map((tripPlan) => (
            <div key={tripPlan.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{tripPlan.tripName}</h3>
                  {/* Show role indicator */}
                  {tripPlan.userId === currentUser.uid ? (
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                      👑 เจ้าของทริป
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full mt-1">
                      👥 ผู้ช่วยจัดทริป
                    </span>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(tripPlan.status)}`}>
                  {getStatusText(tripPlan.status)}
                </span>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-2">📅</span>
                  {formatDate(tripPlan.tripDate)}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-2">📍</span>
                  {tripPlan.location || 'ยังไม่ระบุ'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-2">👥</span>
                  {tripPlan.participantCount || 0} คน
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-2">👤</span>
                  {tripPlan.createdBy || 'ไม่ระบุ'}
                </div>
              </div>

              {/* Trip Creator Section */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="mr-2">👑</span>
                  <span className="text-sm font-medium text-blue-800">ผู้สร้างทริป</span>
                </div>
                <div className="flex items-center bg-white px-3 py-2 rounded border border-blue-200">
                  <span className="text-xs text-green-600 mr-1">✓</span>
                  <HelperDisplayName helperEmail={tripPlan.userName || tripPlan.userId || ''} />
                  <span className="text-xs text-gray-500 mr-3">({tripPlan.userName || tripPlan.userId || 'ไม่ระบุ'})</span>
                  <HelperGirlProfiles 
                    helperEmail={tripPlan.userName || tripPlan.userId || ''} 
                    tripPlanId={tripPlan.id} 
                  />
                </div>
                {/* Debug info */}
                <div className="text-xs text-gray-400 mt-1">
                  Debug: userName={tripPlan.userName}, userId={tripPlan.userId}, id={tripPlan.id}
                </div>
              </div>

              {/* Helpers Section */}
              {(tripPlan.helpers && tripPlan.helpers.length > 0) && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center mb-2">
                    <span className="mr-2">👥</span>
                    <span className="text-sm font-medium text-purple-800">ผู้ช่วยจัดทริป</span>
                    <span className="ml-2 px-2 py-1 bg-purple-200 text-purple-800 text-xs rounded-full">
                      {tripPlan.helpers.length} คน
                    </span>
                  </div>
                  <div className="space-y-2">
                    {tripPlan.helpers.map((helper, index) => (
                      <div key={index} className="flex items-center bg-white px-3 py-2 rounded border border-purple-200">
                        <span className="text-xs text-green-600 mr-1">✓</span>
                        <HelperDisplayName helperEmail={helper.email} />
                        <span className="text-xs text-gray-500 mr-3">({helper.email})</span>
                        <HelperGirlProfiles 
                          helperEmail={helper.email} 
                          tripPlanId={tripPlan.id} 
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">ความคืบหน้า</span>
                  <span className="text-sm font-medium text-gray-600">
                    {getProgressFromChecklist(tripPlan.checklist)}%
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(getProgressFromChecklist(tripPlan.checklist), 100)}%` }}
                  ></div>
                </div>
                
                {/* 3 Step Journey Progress */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex flex-col items-start">
                    <div className="flex items-center mb-2 ml-2">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        getStepProgress(tripPlan.checklist?.planTrip || {}) === 100 ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <span className={`text-xs font-semibold ${
                        isStepActive(1, tripPlan.checklist) ? 'text-gray-600' : 'text-gray-400'
                      }`}>Plan Trip</span>
                    </div>
                    {/* Step 1 Details */}
                    <div className="flex flex-col items-start space-y-1 text-xs ml-6">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.planTrip?.tripCreated ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.planTrip?.tripCreated 
                            ? 'text-gray-600' 
                            : isStepActive(1, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          สร้าง Trip
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.planTrip?.dateConfirmed ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.planTrip?.dateConfirmed 
                            ? 'text-gray-600' 
                            : isStepActive(1, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          วันที่
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.planTrip?.budgetSet ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.planTrip?.budgetSet 
                            ? 'text-gray-600' 
                            : isStepActive(1, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          งบประมาณ
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="flex items-center mb-2 ml-2">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        getStepProgress(tripPlan.checklist?.prepareTrip || {}) === 100 ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <span className={`text-xs font-semibold ${
                        isStepActive(2, tripPlan.checklist) ? 'text-gray-600' : 'text-gray-400'
                      }`}>Prepare Trip</span>
                    </div>
                    {/* Step 2 Details */}
                    <div className="flex flex-col items-start space-y-1 text-xs ml-6">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.prepareTrip?.accommodationBooked ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.prepareTrip?.accommodationBooked 
                            ? 'text-gray-600' 
                            : isStepActive(2, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          ที่พัก
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.prepareTrip?.restaurantBooked ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.prepareTrip?.restaurantBooked 
                            ? 'text-gray-600' 
                            : isStepActive(2, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          ร้านอาหาร
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-1 ${
                            tripPlan.checklist?.prepareTrip?.girlsBooked ? 'bg-green-500' : 'bg-gray-300'
                          }`}></div>
                          <span className={`${
                            tripPlan.checklist?.prepareTrip?.girlsBooked 
                              ? 'text-gray-600' 
                              : isStepActive(2, tripPlan.checklist) 
                                ? 'text-red-500 font-medium animate-pulse'
                                : 'text-gray-400'
                          }`}>
                            จองคิวน้อง
                          </span>
                        </div>
                        
                        {/* Show booked girls when confirmed */}
                        {tripPlan.checklist?.prepareTrip?.girlsBooked && (
                          <div className="ml-2">
                            <BookedGirlDisplay 
                              tripPlanId={tripPlan.id} 
                              requiredGirls={tripPlan.requiredGirls || 1}
                            />
                          </div>
                        )}
                        
                        {/* Show girl cards preview when not booked or when checklist is not available */}
                        {(!tripPlan.checklist?.prepareTrip?.girlsBooked) && (
                          <div className="ml-2">
                            <BookedGirlDisplay 
                              tripPlanId={tripPlan.id} 
                              requiredGirls={tripPlan.requiredGirls || 1}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="flex items-center mb-2 ml-2">
                      <div className={`w-3 h-3 rounded-full mr-2 ${
                        getStepProgress(tripPlan.checklist?.activityTrip || {}) === 100 ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <span className={`text-xs font-semibold ${
                        isStepActive(3, tripPlan.checklist) ? 'text-gray-600' : 'text-gray-400'
                      }`}>Activity Trip</span>
                    </div>
                    {/* Step 3 Details */}
                    <div className="flex flex-col items-start space-y-1 text-xs ml-6">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.activityTrip?.tripConfirmed ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.activityTrip?.tripConfirmed 
                            ? 'text-gray-600' 
                            : isStepActive(3, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          ยืนยันทริป
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.activityTrip?.girlsCheckedIn ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.activityTrip?.girlsCheckedIn 
                            ? 'text-gray-600' 
                            : isStepActive(3, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          น้อง check-in
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          tripPlan.checklist?.activityTrip?.reviewSubmitted ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className={`${
                          tripPlan.checklist?.activityTrip?.reviewSubmitted 
                            ? 'text-gray-600' 
                            : isStepActive(3, tripPlan.checklist) 
                              ? 'text-red-500 font-medium animate-pulse'
                              : 'text-gray-400'
                        }`}>
                          รีวิวทริป,น้อง
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewTripDetails(tripPlan)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm"
                  >
                    ข้อมูล Trip
                  </button>
                  <button
                    onClick={() => handleUpdateChecklist(tripPlan)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm"
                  >
                    อัพเดท Trip
                  </button>
                  
                  {/* Only show invite and management buttons for trip owners */}
                  {tripPlan.userId === currentUser.uid && (
                    <>
                      <button
                        onClick={() => handleInviteHelper(tripPlan)}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition text-sm"
                      >
                        เชิญเพื่อน
                      </button>
                      {(tripPlan.helpers && tripPlan.helpers.length > 0) && (
                        <button
                          onClick={() => handleManageHelpers(tripPlan)}
                          className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition text-sm"
                        >
                          จัดการผู้ช่วย
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteTripPlan(tripPlan.id)}
                        disabled={deletingTripId === tripPlan.id || !tripPlan.id}
                        className={`px-4 py-2 rounded-lg transition text-sm ${
                          deletingTripId === tripPlan.id || !tripPlan.id
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                      >
                        {deletingTripId === tripPlan.id ? 'กำลังลบ...' : 'ลบ Trip'}
                      </button>
                    </>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  สร้างเมื่อ {formatDate(tripPlan.createdAt)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {filteredTripPlans.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {tripPlans.length === 0 ? 'ยังไม่มีทริป' : 'ไม่มีทริปที่ตรงกับเงื่อนไข'}
            </h3>
            <p className="text-gray-600 mb-4">
              {tripPlans.length === 0 ? 'เริ่มต้นสร้างทริปแรกของคุณ' : 'ลองเปลี่ยนตัวกรองหรือสร้างทริปใหม่'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              สร้างทริปใหม่
            </button>
          </div>
        )}
      </div>

      {/* Create Trip Modal */}
        {showCreateModal && (
          <TripPlanModal
            isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          mode="create"
        />
      )}

      {/* Edit Trip Modal */}
      {showEditModal && selectedTripPlan && (
        <TripPlanModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTripPlan(null);
          }}
          mode="edit"
          tripPlan={selectedTripPlan}
        />
      )}

      {/* Checklist Modal */}
      {showChecklistModal && selectedTripPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  อัพเดท Checklist - {selectedTripPlan.tripName}
                </h2>
                <button
                  onClick={() => {
                    setShowChecklistModal(false);
                    setSelectedTripPlan(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <TripChecklist 
                tripPlan={selectedTripPlan} 
                onUpdate={(updatedPlan) => {
                  setSelectedTripPlan(updatedPlan);
                  // Update the trip in the list
                  setTripPlans(prev => 
                    prev.map(plan => 
                      plan.id === updatedPlan.id ? updatedPlan : plan
                    )
                  );
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Invite Helper Modal */}
      {showInviteModal && selectedTripPlan && (
        <SimpleInviteModal
          isOpen={showInviteModal}
          onClose={() => {
            setShowInviteModal(false);
            setSelectedTripPlan(null);
          }}
          tripPlanId={selectedTripPlan.id}
          tripName={selectedTripPlan.tripName}
        />
      )}

      {/* Helper Management Modal */}
      {showHelperModal && selectedTripPlan && (
        <HelperManagementModal
          isOpen={showHelperModal}
          onClose={() => {
            setShowHelperModal(false);
            setSelectedTripPlan(null);
          }}
          tripPlan={selectedTripPlan}
        />
      )}
    </div>
  );
};

export default TripPlansPage;